import {
  mode,
  StyleFunctionProps,
  transparentize,
} from "@chakra-ui/theme-tools";

const chakraDefaultProps = {
  colorScheme: "gray",
};

const defaultProps = {
  ...chakraDefaultProps,
  color: "fg-main",
};

const chakraSizes = {
  lg: {
    field: {
      fontSize: "md",
      borderRadius: "lg",
    },
  },
};

// baseStyle does not work, so use default size
const sizes = {
  ...chakraSizes,
  // The default size is `"md"`
  md: {
    addon: {
      height: "3rem",
    },
    field: {
      height: "3rem",
    },
  },
};

const chakraVariants = {
  outline: (props: StyleFunctionProps) => ({
    field: {
      borderRadius: "lg",
      bg: mode("white", "gray.800")(props),
      _hover: { borderColor: mode("gray.300", "gray.600")(props) },
      _focus: {
        borderColor: mode("brand.500", "brand.200")(props),
        boxShadow: mode(
          `0px 0px 0px 1px ${transparentize(`brand.500`, 1.0)(props.theme)}`,
          `0px 0px 0px 1px ${transparentize(`brand.200`, 1.0)(props.theme)}`,
        )(props),
      },
    },
    addon: {
      borderRadius: "lg",
      bg: mode("gray.50", "gray.700")(props),
    },
  }),
  "outline-accent": {
    field: {
      bg: "white",
      borderRadius: "lg",
      color: "fg-main",
      borderWidth: "1px",
      borderColor: "fg-accent",
      _placeholder: {
        color: "gray.500",
      },
      _hover: {
        borderColor: "fg-accent",
      },
      _focus: {
        borderColor: "fg-accent",
      },
    },
  },
  "outline-on-accent": (props: StyleFunctionProps) => ({
    field: {
      bg: "white",
      borderRadius: "lg",
      color: "gray.900",
      borderWidth: "1px",
      borderColor: "brand.50",
      _placeholder: {
        color: "gray.500",
      },
      _hover: {
        borderColor: "brand.100",
      },
      _focus: {
        borderColor: "brand.200",
        boxShadow: `0px 0px 0px 1px ${transparentize(
          `brand.200`,
          1.0,
        )(props.theme)}`,
      },
    },
  }),
  filled: (props: StyleFunctionProps) => {
    if (props.colorScheme === "gray") {
      return {
        field: {
          bg: mode("white", "gray.800")(props),
          _hover: {
            borderColor: mode("gray.200", "gray.700")(props),
            bg: mode("white", "gray.700")(props),
          },
          _focus: {
            borderColor: "accent",
            bg: mode("white", "gray.800")(props),
          },
        },
      };
    }
    return {
      field: {
        bg: "bg-accent-subtle",
        color: "on-accent",
        _placeholder: {
          color: "on-accent",
        },
        _hover: {
          borderColor: "brand.400",
          bg: "bg-accent-subtle",
        },
        _focus: {
          bg: "bg-accent-subtle",
          borderColor: "brand.300",
        },
      },
    };
  },
};

const variants = {
  ...chakraVariants,
  outline: {
    field: {
      _placeholder: {
        color: "gray",
      },
      background: "white",
      backgroundColor: "white",
      border: "1px solid",
      borderColor: "gray.300",
      borderRadius: "lg",
      color: "fg-main",
      height: "3rem",
    },
  },
  stripe: (props: StyleFunctionProps) => ({
    field: {
      _placeholder: {
        color: "th.gray4",
      },
      "--webkit-font-smoothing": "antialiased",
      border: props.isInvalid ? "2px solid" : "1px solid",
      borderColor: props.isInvalid ? "th.danger1" : "#e6e6e6",
      borderRadius: "5px",
      boxShadow: "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
      color: "rgb(78, 84, 76)",
      fontFamily: "Moderat, sans-serif",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "18.4px",
      p: "12px",
      _focus: {
        borderColor: "hsla(210, 96%, 45%, 50%)",
        boxShadow:
          "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02), 0 0 0 3px hsla(210, 96%, 45%, 25%), 0 1px 1px 0 rgba(0, 0, 0, 0.08)",
        outline: 0,
      },
    },
  }),
  "th-short-text": (props: StyleFunctionProps) => {
    return {
      field: {
        _placeholder: {
          color: "th.gray4",
        },
        _focus: {
          borderColor: props.isInvalid ? "th.danger1" : "th.actionLavender2",
          boxShadow: props.isInvalid
            ? "initial"
            : "0px 1px 2px 0px var(--chakra-colors-newLavender-100)",
        },
        border: props.isInvalid ? "2px solid" : "1px solid",
        borderColor: props.isInvalid ? "th.danger1" : "th.cream3",
        borderRadius: "lg",
        fontFamily: "Moderat",
        fontWeight: "light",
        fontSize: "1.125rem",
        lineHeight: "140%",
        letterSpacing: "0",
      },
    };
  },
};

// ref: https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/input.ts
export const Input = {
  defaultProps,
  sizes,
  variants,
};
